/**
 * The spec the playground is built from: the API's own `/v1/openapi.json`,
 * or, when that cannot be had, the snapshot the build copied next to the
 * page (`/spec.json`), shown with a "spec: snapshot" badge.
 *
 * It is read again every five minutes and whenever the openapi Lens is
 * dropped; a document whose text changed deals a new catalogue to every
 * listener, so new operations appear as cards without a reload. A failed
 * re-read keeps the catalogue already held.
 *
 * A read that does not answer within ten seconds has failed: at boot the
 * snapshot is read instead, and a re-read keeps what is held. The next
 * re-read is armed when this one starts, so a hung one cannot stop them.
 */

import type { CreateSpecSource, Json, SpecSource, Unsub } from '../contracts';
import { catalogueOf, fingerprintOf, type SpecCatalogue } from './ops';

import type { Timers } from '../net/presence';
import { isObj } from '../model/json';

export const REVALIDATE_MS = 5 * 60_000;
export const SNAPSHOT_URL = '/spec.json';
/** How long one read of the document may take before it counts as failed. */
export const SPEC_TIMEOUT_MS = 10_000;

export interface SpecOptions {
  readonly every?: number;
  readonly timers?: Timers;
  readonly snapshotUrl?: string;
  readonly timeoutMs?: number;
}

const isSpec = (v: Json): boolean => isObj(v) && typeof v.openapi === 'string' && isObj(v.paths);

/**
 * One document, fetched and read; null when it cannot be had, is not an
 * OpenAPI document, or has not arrived (headers and body) within `ms`.
 */
const fetchSpec = async (fetchFn: typeof fetch, url: string, source: 'live' | 'snapshot', timers: Timers, ms: number): Promise<SpecCatalogue | null> => {
  const stop = new AbortController();
  let timer: unknown = null;
  const expired = new Promise<null>((resolve) => { timer = timers.set(() => { stop.abort(); resolve(null); }, ms); });
  const read = (async () => {
    try {
      const response = await fetchFn(url, { headers: { accept: 'application/json' }, signal: stop.signal });
      if (!response.ok) return null;
      const text = await response.text();
      const doc = JSON.parse(text) as Json;
      return isSpec(doc) ? catalogueOf(doc, source, fingerprintOf(text)) : null;
    } catch {
      return null;
    }
  })();
  try {
    return await Promise.race([read, expired]);
  } finally {
    timers.clear(timer);
  }
};

/** A SpecSource that says which catalogue it holds, and can be stopped. */
export interface LoadedSpec extends SpecSource {
  current(): SpecCatalogue;
  stop(): void;
}

export const createSpecSource: CreateSpecSource = (apiBase, fetchFn) => loadSpec(apiBase, fetchFn);

export async function loadSpec(apiBase: string, fetchFn: typeof fetch = globalThis.fetch.bind(globalThis), options: SpecOptions = {}): Promise<LoadedSpec> {
  const base = apiBase.trim().replace(/\/+$/, '');
  const live = `${base}/v1/openapi.json`;
  const snapshotUrl = options.snapshotUrl ?? SNAPSHOT_URL;
  const every = options.every ?? REVALIDATE_MS;
  const timers: Timers = options.timers ?? { set: (fn, ms) => setTimeout(fn, ms), clear: (id) => clearTimeout(id as ReturnType<typeof setTimeout>) };
  const ms = options.timeoutMs ?? SPEC_TIMEOUT_MS;

  const first = (await fetchSpec(fetchFn, live, 'live', timers, ms)) ?? (await fetchSpec(fetchFn, snapshotUrl, 'snapshot', timers, ms));
  if (!first) throw new Error(`no OpenAPI document at ${live}, nor the build's snapshot at ${snapshotUrl}`);
  let current: SpecCatalogue = first;
  const listeners = new Set<(c: SpecCatalogue) => void>();
  let timer: unknown = null;
  let stopped = false;

  /** The read out now, which a second ask joins rather than starting another. */
  let reading: Promise<SpecCatalogue> | null = null;
  const reload = (): Promise<SpecCatalogue> => {
    reading ??= (async () => {
      try {
        const next = await fetchSpec(fetchFn, live, 'live', timers, ms);
        if (next && (next.fingerprint !== current.fingerprint || next.source !== current.source)) {
          current = next;
          for (const fn of [...listeners]) { try { fn(current); } catch { /* the listener's own fault */ } }
        }
        return current;
      } finally {
        reading = null;
      }
    })();
    return reading;
  };

  const arm = () => {
    if (stopped) return;
    timer = timers.set(() => { timer = null; arm(); void reload(); }, every);
  };
  arm();

  return {
    current: () => current,
    reload,
    on(fn): Unsub {
      listeners.add(fn);
      return () => { listeners.delete(fn); };
    },
    stop() {
      stopped = true;
      if (timer !== null) timers.clear(timer);
      timer = null;
    },
  };
}
